import { Controller } from "@hotwired/stimulus"
import "jquery"

export default class extends Controller {
  static targets = ["profitField", "finalCostField", "finishProductPriceField", "unitField"]

  connect() {
    this.finishProductPrice = 0
    this.finishProductUnit = "piece"
    this.initializeCocoonCallbacks()
    this.initializeFinishProductListener()
    this.calculateFinalCost()
  }

  initializeCocoonCallbacks() {
    $(this.element).on('cocoon:after-insert', () => {
      // Small delay to ensure the new field is in the DOM
      setTimeout(() => {
        this.updateFinalCost()
      }, 50)
    })

    $(this.element).on('cocoon:after-remove', () => {
      this.updateFinalCost()
    })
  }

  initializeFinishProductListener() {
    // Listen for finish product selection changes
    const finishProductSelect = this.element.querySelector('select[name*="[finish_product_id]"]')
    if (finishProductSelect) {
      // Handle Select2 changes
      $(finishProductSelect).on('select2:select', (e) => {
        this.handleFinishProductChange(e.params.data.id)
      })

      $(finishProductSelect).on('select2:clear', () => {
        this.finishProductPrice = 0
        this.finishProductUnit = "piece"
        if (this.hasFinishProductPriceFieldTarget) {
          this.finishProductPriceFieldTarget.value = "0.00"
        }
        if (this.hasUnitFieldTarget) {
          this.unitFieldTarget.value = ""
        }
        this.updateFinalCost()
      })

      // Handle regular select changes (fallback)
      finishProductSelect.addEventListener('change', (e) => {
        if (e.target.value) {
          this.handleFinishProductChange(e.target.value)
        } else {
          this.finishProductPrice = 0
          this.finishProductUnit = "piece"
          if (this.hasFinishProductPriceFieldTarget) {
            this.finishProductPriceFieldTarget.value = "0.00"
          }
          if (this.hasUnitFieldTarget) {
            this.unitFieldTarget.value = ""
          }
          this.updateFinalCost()
        }
      })

      // If there's already a selected product, fetch its price
      if (finishProductSelect.value) {
        this.handleFinishProductChange(finishProductSelect.value)
      }
    }
  }

  async handleFinishProductChange(productId) {
    const companyId = this.element.dataset.companyId

    if (!productId) {
      this.finishProductPrice = 0
      this.finishProductUnit = "piece"
      if (this.hasFinishProductPriceFieldTarget) {
        this.finishProductPriceFieldTarget.value = "0.00"
      }
      if (this.hasUnitFieldTarget) {
        this.unitFieldTarget.value = ""
      }
      this.updateFinalCost()
      return
    }

    try {
      const response = await fetch(`/api/v1/companies/${companyId}/finish_products/${productId}`)
      const data = await response.json()
      this.finishProductPrice = parseFloat(data.price) || 0
      this.finishProductUnit = data.unit || "piece"

      // Update the finish product price display field
      if (this.hasFinishProductPriceFieldTarget) {
        this.finishProductPriceFieldTarget.value = this.finishProductPrice.toFixed(2)
      }

      // Update the unit display field
      if (this.hasUnitFieldTarget) {
        this.unitFieldTarget.value = this.finishProductUnit.charAt(0).toUpperCase() + this.finishProductUnit.slice(1)
      }

      this.updateFinalCost()
    } catch (error) {
      console.error('Error fetching finish product price:', error)
      this.finishProductPrice = 0
      this.finishProductUnit = "piece"
      if (this.hasFinishProductPriceFieldTarget) {
        this.finishProductPriceFieldTarget.value = "0.00"
      }
      if (this.hasUnitFieldTarget) {
        this.unitFieldTarget.value = ""
      }
      this.updateFinalCost()
    }
  }

  calculateFinalCost() {
    // Set up event delegation for overhead cost price changes (similar to products controller)
    // This needs to be set up here to ensure it works for dynamically added fields
    if (!this.eventDelegationSetup) {
      // Use event delegation to catch overhead cost price changes
      // This works for both existing and dynamically added fields (via Cocoon)
      // Check for the attribute, dataset property, or name attribute pattern
      this.element.addEventListener('input', (e) => {
        const target = e.target
        if (target && (
          target.hasAttribute('data-overhead-cost-price') ||
          target.dataset.overheadCostPrice !== undefined ||
          (target.name && target.name.includes('overhead_costs_attributes') && target.name.includes('price'))
        )) {
          this.overheadCostChanged()
        }
      })

      this.element.addEventListener('change', (e) => {
        const target = e.target
        if (target && (
          target.hasAttribute('data-overhead-cost-price') ||
          target.dataset.overheadCostPrice !== undefined ||
          (target.name && target.name.includes('overhead_costs_attributes') && target.name.includes('price'))
        )) {
          this.overheadCostChanged()
        }
      })

      // Also listen for raw material total changes via event delegation
      this.element.addEventListener('input', (e) => {
        if (e.target && e.target.hasAttribute('data-raw-material-calculator-target') &&
          e.target.getAttribute('data-raw-material-calculator-target') === 'total') {
          this.rawMaterialTotalChanged()
        }
      })

      this.eventDelegationSetup = true
    }

    this.updateFinalCost()
  }

  updateFinalCost() {
    // Formula: final_cost = (raw_material * quantity + raw_material * quantity) + overhead_cost
    // Each raw material total is already calculated as: price * quantity in raw_material_calculator_controller.js
    let totalCost = 0

    // Sum up all raw material totals (each is: raw_material_price * quantity)
    const rawMaterialTotals = this.element.querySelectorAll('input[data-raw-material-calculator-target="total"]')
    rawMaterialTotals.forEach(field => {
      const value = parseFloat(field.value) || 0
      totalCost += value
    })

    // Multiply raw material total by 12 if finish product unit is "dozen"
    // Raw material totals are per piece; for dozen we need cost per 12 pieces
    const unitIsDozen = this.finishProductUnit && this.finishProductUnit.toLowerCase() === "dozen"
    if (unitIsDozen) {
      totalCost = totalCost * 12
    }

    // Sum up all overhead cost prices
    // Try multiple selectors to ensure we catch all overhead cost price fields
    const overheadCostPrices = this.element.querySelectorAll('input[data-overhead-cost-price], input[name*="[overhead_costs_attributes]"][name*="[price]"]')
    let overheadTotal = 0
    overheadCostPrices.forEach((priceField) => {
      // Skip fields that are marked for destruction
      const nestedFields = priceField.closest('.nested-fields')
      if (nestedFields) {
        const destroyField = nestedFields.querySelector('input[name*="[_destroy]"]')
        if (destroyField && destroyField.value === '1') {
          return
        }
      }
      const price = parseFloat(priceField.value) || 0
      if (!isNaN(price)) {
        overheadTotal += price
      }
    })
    totalCost += overheadTotal

    // Final cost = sum(raw_material * quantity) + overhead_cost
    const finalCost = totalCost

    // Formula: profit = price of bolt production finish product - final_cost
    const profit = this.finishProductPrice - finalCost

    // Update final cost field
    if (this.hasFinalCostFieldTarget) {
      this.finalCostFieldTarget.value = finalCost.toFixed(2)
    }

    // Update profit field (auto-calculated)
    if (this.hasProfitFieldTarget) {
      this.profitFieldTarget.value = profit.toFixed(2)
    }
  }

  // Called when raw material total changes
  rawMaterialTotalChanged() {
    setTimeout(() => {
      this.updateFinalCost()
    }, 100) // Small delay to ensure the total has been updated
  }

  // Called when profit changes (should not happen as it's readonly, but keep for safety)
  profitChanged() {
    // Profit is auto-calculated, so we recalculate everything
    this.updateFinalCost()
  }

  // Called when a raw material is removed
  rawMaterialRemoved() {
    this.updateFinalCost()
  }

  // Called when overhead cost price changes
  overheadCostChanged() {
    // Immediately update to ensure responsiveness
    this.updateFinalCost()
  }
}
